/**
 * OperatorQueuePage — F-007 Phase 4 (IMPL-P4-005).
 *
 * Landing della dashboard operatore: lista delle pratiche `INVIATA` (FIFO)
 * con auto-refresh ogni 15s e bottone "Prendi in carico" per riga.
 *
 * Composizione:
 *   - `OperatorLayout` (chrome globale + titolo)
 *   - `HeartbeatHeader` (timer "Aggiornato Xs fa")
 *   - `OperatorQueueTable` (lista + take button)
 *
 * 409 race-condition handling: delegato a `useTakeInCarico` (toast.error
 * + invalidation della queue).
 */
import * as React from 'react';
import { OperatorLayout } from '../components/OperatorLayout';
import { OperatorQueueTable } from '../components/OperatorQueueTable';
import { HeartbeatHeader } from '../components/HeartbeatHeader';
import { useOperatorQueue } from '../hooks/useOperatorQueue';
import { useTakeInCarico } from '../hooks/useTakeInCarico';

export function OperatorQueuePage() {
  const [page] = React.useState(1);
  const [pageSize] = React.useState(20);
  const queueQuery = useOperatorQueue(page, pageSize);
  const takeMutation = useTakeInCarico();
  const [pendingId, setPendingId] = React.useState<string | null>(null);

  const handleTake = (praticaId: string) => {
    setPendingId(praticaId);
    takeMutation.mutate(praticaId, {
      onSettled: () => {
        setPendingId(null);
      },
    });
  };

  return (
    <OperatorLayout
      title="Coda operatore"
      description="Pratiche inviate dai clienti in attesa di lavorazione."
    >
      <div className="flex items-center justify-between">
        <HeartbeatHeader dataUpdatedAt={queueQuery.dataUpdatedAt} />
        <p className="text-xs text-muted-foreground">
          Auto-refresh ogni 15s
        </p>
      </div>

      {queueQuery.isLoading && (
        <p
          role="status"
          aria-live="polite"
          className="text-sm text-muted-foreground"
        >
          Caricamento coda…
        </p>
      )}

      {queueQuery.isError && (
        <p
          role="alert"
          className="rounded-md border border-destructive/50 bg-destructive/10 px-3 py-2 text-sm text-destructive"
        >
          Impossibile caricare la coda. Riprova fra qualche secondo.
        </p>
      )}

      {queueQuery.data && (
        <OperatorQueueTable
          items={queueQuery.data.items}
          onTake={handleTake}
          takePending={takeMutation.isPending}
          pendingId={pendingId}
        />
      )}
    </OperatorLayout>
  );
}
